import {useEffect, useMemo} from 'react'

import {getState, useSelector} from '../../store.js'
import {
  ACCOUNTING,
  API_KEYS,
  BARCODE_SCANNERS,
  BILLING,
  CONTACTS,
  GENERAL,
  PACKING_LISTS,
  PAYMENTS,
  PRESETS,
  PRINTING,
  PURCHASE_ORDERS,
  RETURN_ORDER_CODES,
  RULES,
  SALES_CHANNELS,
  SCALES,
  SHIPPERS,
  SHIPPING_INSURANCE,
  SUPPLIERS,
  THREE_P_L,
  USERS,
  WAREHOUSES,
} from '../../common/constants/SettingsPanels.js'
import className from '../../common/className.js'
import {companyIDSelector} from '../../data/company.js'
import {
  currentSettingsPanelSelector,
  settingsPanelOptionsSelector,
} from './settingsFunctions.js'
import Accounting from './Accounting/index.js'
import APIKeys from './APIKeys/index.js'
import BarcodeScanners from './BarcodeScanners/index.js'
import Billing from './Billing/index.js'
import General from './General/index.js'
import PackingLists from './PackingLists/index.js'
import Payments from './Payments/index.js'
import Presets from './Presets/index.js'
import Printing from './Printing/index.js'
import ReturnOrderCodes from './ReturnOrderCodes/index.js'
import Rules from './Rules/index.js'
import SalesChannels from './SalesChannels/index.js'
import Scales from './Scales/index.js'
import Shippers from './Shippers/index.js'
import ShippingInsurance from './ShippingInsurance/index.js'
import Suppliers from './Suppliers/index.js'
import ThirdPartyLogistics from './ThirdPartyLogistics/index.js'
import Users from './Users/index.js'
import Warehouses from './Warehouses/index.js'
import Modals from './Modals/index.js'
import PurchaseOrders from './PurchaseOrders/index.js'
import Contacts from './Contacts/index.js'
import useStable from '../../common/useStable.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import Icon from '../../common/icons/Icon.js'
import {
  debouncedSaveUserSettings,
  pinnedSettingsPagesSelector,
} from '../../data/me.js'

const panels = {
  [ACCOUNTING]: Accounting,
  [API_KEYS]: APIKeys,
  [BARCODE_SCANNERS]: BarcodeScanners,
  [BILLING]: Billing,
  [CONTACTS]: Contacts,
  [GENERAL]: General,
  [PACKING_LISTS]: PackingLists,
  [PAYMENTS]: Payments,
  [PRESETS]: Presets,
  [PRINTING]: Printing,
  [PURCHASE_ORDERS]: PurchaseOrders,
  [RETURN_ORDER_CODES]: ReturnOrderCodes,
  [RULES]: Rules,
  [SALES_CHANNELS]: SalesChannels,
  [SCALES]: Scales,
  [SHIPPERS]: Shippers,
  [SHIPPING_INSURANCE]: ShippingInsurance,
  [SUPPLIERS]: Suppliers,
  [THREE_P_L]: ThirdPartyLogistics,
  [USERS]: Users,
  [WAREHOUSES]: Warehouses,
}

function togglePin(settingPanel) {
  const oldPinned = pinnedSettingsPagesSelector(getState())

  const pinned = oldPinned.filter((v) => v !== settingPanel)

  if (oldPinned.length === pinned.length) {
    pinned.push(settingPanel)
  }

  debouncedSaveUserSettings({'settings.pinned': pinned})
}

function SettingsNavItem({item}) {
  const disabled = item.disabled

  return (
    <dd className={className`list__item--side-nav flex--justify ${{disabled}}`}>
      <a
        className={className`link--side-nav  ${{
          'link--side-nav-active': item.active,
          'w-80': item.canPin,
          disabled,
        }}`}
        href={item.url}
        title={item.display}
        disabled={disabled}
      >
        <span>{item.display}</span>
      </a>
      {item.canPin && (
        <ButtonLink
          className={className`no-underline margin-right-5 ${!item.pinned ? 'unpinned' : 'pinned'}`}
          title={
            item.pinned
              ? 'Unpin item from Settings Menu'
              : 'Pin item to Settings Menu'
          }
          onClick={() => togglePin(item.value)}
        >
          <Icon name="thumbtack" className="fs-01" />
        </ButtonLink>
      )}
    </dd>
  )
}

function getSettingsPanelStructure(options) {
  const structure = {}

  for (const option of options) {
    const sectionObj = structure[option.section] || {
      section: option.section,
      items: [],
    }

    sectionObj.items.push(option)

    structure[option.section] = sectionObj
  }

  return Object.values(structure)
}

export default function Settings() {
  useEffect(() => {
    document.title = 'Settings'
  }, [])
  const currentSettingsPanel = useSelector(currentSettingsPanelSelector)
  const companyID = useSelector(companyIDSelector)
  const settingsPanelOptions = useStable(
    useSelector(settingsPanelOptionsSelector),
  )
  const settingsPanelStructure = useMemo(
    () => getSettingsPanelStructure(settingsPanelOptions),
    settingsPanelOptions,
  )

  const Panel = panels[currentSettingsPanel]

  return (
    <section className="wrap--container" role="main">
      <Modals />
      <div className="wrap--row">
        <div className="medium-9 medium-push-2 columns padding-top-10">
          {Panel && <Panel />}
        </div>
        <div className="medium-2 medium-pull-10 columns">
          <div className="inner-wrap--side-nav">
            <div className="alert alert--standard alert--sm alert--support-pin align-center">
              <strong className="fs-n0">Support Pin: </strong>
              <span className="fs-n0">{companyID}</span>
            </div>
            {settingsPanelStructure.map(({section, items}) => (
              <dl key={section} className="list--side-nav">
                <dt className="list__title--side-nav">{section}</dt>
                {items.map((item) => (
                  <SettingsNavItem key={item.value} item={item} />
                ))}
              </dl>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// const oldShit = (
//   <>
//     <dl className="list--side-nav">
//       <dt className="list__title--side-nav">Account Settings</dt>
//       <SettingsNavItem
//         title="General"
//         resource={GENERAL}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[GENERAL]}
//       />
//       <SettingsNavItem
//         title="Users"
//         resource={USERS}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[USERS]}
//       />
//       {useContacts && (
//         <SettingsNavItem
//           title="Contacts"
//           resource={CONTACTS}
//           currentSettingsPanel={currentSettingsPanel}
//           disabled={!settingsPermissions[CONTACTS]}
//         />
//       )}
//       <SettingsNavItem
//         title="Billing"
//         resource={BILLING}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[BILLING]}
//       />
//       {showAPIKeysNav && (
//         <SettingsNavItem
//           title="API Keys"
//           resource={API_KEYS}
//           currentSettingsPanel={currentSettingsPanel}
//           disabled={!settingsPermissions[API_KEYS]}
//         />
//       )}
//     </dl>

//     <dl className="list--side-nav">
//       <dt className="list__title--side-nav">Fulfillment</dt>
//       <SettingsNavItem
//         title="Warehouses"
//         resource={WAREHOUSES}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[WAREHOUSES]}
//       />
//       {showSuppliersNav && (
//         <SettingsNavItem
//           title="Suppliers"
//           resource={SUPPLIERS}
//           currentSettingsPanel={currentSettingsPanel}
//           disabled={!settingsPermissions[SUPPLIERS]}
//         />
//       )}
//       <SettingsNavItem
//         title="3PLs"
//         resource={THREE_P_L}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[THREE_P_L]}
//       />
//     </dl>
//     <dl className="list--side-nav">
//       <dt className="list__title--side-nav">Integrations</dt>
//       <SettingsNavItem
//         title="Sales Channels"
//         resource={SALES_CHANNELS}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[SALES_CHANNELS]}
//       />
//       <SettingsNavItem
//         title="Shippers/Carriers"
//         resource={SHIPPERS}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[SHIPPERS]}
//       />
//       <SettingsNavItem
//         title="Accounting"
//         resource={ACCOUNTING}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[ACCOUNTING]}
//       />
//       <SettingsNavItem
//         title="Payments"
//         resource={PAYMENTS}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[PAYMENTS]}
//       />
//       <SettingsNavItem
//         title="Shipping Insurance"
//         resource={SHIPPING_INSURANCE}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[SHIPPING_INSURANCE]}
//       />
//     </dl>
//     <dl className="list--side-nav">
//       <dt className="list__title--side-nav">Profiles</dt>
//       <SettingsNavItem
//         title="General"
//         href={`${PACKING_LIST_SETTINGS_LINK}/${PROFILES_GENERAL}`}
//         isActive={
//           currentSettingsPanel === PACKING_LISTS &&
//           selectedProfileTab === PROFILES_GENERAL
//         }
//         disabled={!settingsPermissions[PACKING_LISTS]}
//       />
//       <SettingsNavItem
//         title="Packing Lists"
//         href={`${PACKING_LIST_SETTINGS_LINK}/${PROFILES_PACKING_LIST}`}
//         isActive={
//           currentSettingsPanel === PACKING_LISTS &&
//           selectedProfileTab === PROFILES_PACKING_LIST
//         }
//         disabled={!settingsPermissions[PACKING_LISTS]}
//       />
//       {showReturnOrdersNav && (
//         <SettingsNavItem
//           title="Return Emails"
//           href={`${PACKING_LIST_SETTINGS_LINK}/${PROFILES_RETURN_ORDER}`}
//           isActive={
//             currentSettingsPanel === PACKING_LISTS &&
//             selectedProfileTab === PROFILES_RETURN_ORDER
//           }
//           disabled={!settingsPermissions[PACKING_LISTS]}
//         />
//       )}
//       <SettingsNavItem
//         title="Ship From Alias"
//         href={`${PACKING_LIST_SETTINGS_LINK}/${PROFILES_SHIP_FROM_ALIAS}`}
//         isActive={
//           currentSettingsPanel === PACKING_LISTS &&
//           selectedProfileTab === PROFILES_SHIP_FROM_ALIAS
//         }
//         disabled={!settingsPermissions[PACKING_LISTS]}
//       />
//       {useBrandedTracking && (
//         <SettingsNavItem
//           title="Branded Tracking Page"
//           href={`${PACKING_LIST_SETTINGS_LINK}/${PROFILES_BRANDED_TRACKING}`}
//           isActive={
//             currentSettingsPanel === PACKING_LISTS &&
//             selectedProfileTab === PROFILES_BRANDED_TRACKING
//           }
//           disabled={!settingsPermissions[PACKING_LISTS]}
//         />
//       )}
//     </dl>
//     <dl className="list--side-nav">
//       <dt className="list__title--side-nav">Purchase Orders</dt>
//       <SettingsNavItem
//         title="PO ID Format"
//         resource={PURCHASE_ORDERS}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[PURCHASE_ORDERS]}
//       />
//     </dl>
//     {showReturnOrdersNav && (
//       <dl className="list--side-nav">
//         <dt className="list__title--side-nav">Returns</dt>
//         <SettingsNavItem
//           title="Return Codes"
//           resource={RETURN_ORDER_CODES}
//           currentSettingsPanel={currentSettingsPanel}
//           disabled={!settingsPermissions[RETURN_ORDER_CODES]}
//         />
//       </dl>
//     )}
//     <dl className="list--side-nav">
//       <dt className="list__title--side-nav">Devices</dt>
//       <SettingsNavItem
//         title="Printers"
//         resource={PRINTING}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[PRINTING]}
//       />
//       <SettingsNavItem
//         title="Scales"
//         resource={SCALES}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[SCALES]}
//       />
//       <SettingsNavItem
//         title="Barcode Scanners"
//         resource={BARCODE_SCANNERS}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[BARCODE_SCANNERS]}
//       />
//     </dl>
//     <dl className="list--side-nav">
//       <dt className="list__title--side-nav">Automation</dt>
//       <SettingsNavItem
//         title="Presets"
//         resource={PRESETS}
//         currentSettingsPanel={currentSettingsPanel}
//         disabled={!settingsPermissions[PRESETS]}
//       />
//       {showRulesNav && (
//         <SettingsNavItem
//           title="Rules"
//           resource={RULES}
//           currentSettingsPanel={currentSettingsPanel}
//           disabled={!settingsPermissions[RULES]}
//         />
//       )}
//     </dl>
//   </>
// )
